import { CanvasLib } from "@sc07-canvas/lib/canvas";
import { type ClientConfig } from "@sc07-canvas/lib/net";
import { motion, useReducedMotion } from "framer-motion";
import {
  Brush,
  ChevronDown,
  ChevronUp,
  Crosshair,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";

import { useDialog } from "@/contexts/DialogContext";
import { usePanel } from "@/contexts/PanelContext";

import { useAppContext } from "../../contexts/AppContext";
import {
  PALETTE_LAYOUT_SPRING,
  useCompactPalette,
} from "../../hooks/useCompactPalette";
import { useIsMobile } from "../../hooks/useIsMobile";
import { CanvasCore } from "../../lib/canvas";
import { setAvailablePixelsFavicon } from "../../lib/favicon";
import network from "../../lib/network";
import { useSidebarWidth } from "../Header/useSidebarWidth";
import { SIDEBAR_SPRING } from "../SidebarBase";
import { ShareModal } from "./ShareModal";

const getTimeLeft = (available: number, config: ClientConfig) => {
  /**
   * when the user last placed a pixel (unix ts)
   */
  const lastPlace = CanvasCore.get().lastPlace;

  if (!lastPlace) return undefined;

  const cooldown = CanvasLib.getPixelCooldown(config);
  const pixelAvailableAt = lastPlace + cooldown * 1000;

  const pixelCooldown = (Date.now() - pixelAvailableAt) / 1000;

  if (!pixelCooldown) return undefined;
  if (pixelCooldown > 0) return 0;

  return Math.abs(pixelCooldown).toFixed(1);
};

const PlaceCountdown = ({ available }: { available: number }) => {
  const { config } = useAppContext<true>();
  const [timeLeft, setTimeLeft] = useState(getTimeLeft(available, config));

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(getTimeLeft(available, config));
    }, 100);

    return () => {
      clearInterval(timer);
    };
  }, [available]);

  return (
    <>
      {timeLeft
        ? available < config.canvas.pixel.maxStack && timeLeft + "s"
        : ""}
    </>
  );
};

const OnlineCount = () => {
  const [online, setOnline] = useState<number>();

  useEffect(() => {
    function handleOnline(count: number) {
      setOnline(count);
    }

    network.waitForState("online").then(([count]) => setOnline(count));
    network.on("online", handleOnline);

    return () => {
      network.off("online", handleOnline);
    };
  }, []);

  return <>{typeof online === "number" ? online : "???"}</>;
};

export const CanvasMeta = () => {
  const [available, setAvailable] = useState(-1);
  const isMobile = useIsMobile();
  const [collapsed, setCollapsed] = useState(() => isMobile);
  const { canvasPosition, cursor, config, user, settings } =
    useAppContext<true>();
  const Dialog = useDialog();
  const Panel = usePanel();
  const reduceMotion = useReducedMotion() ?? false;
  const sidebarWidth = useSidebarWidth("Left");
  const compactPalette = useCompactPalette();
  const canvasMetaBottom = !isMobile && compactPalette ? 84 : 76;
  const offsetForSidebar = Boolean(
    !isMobile &&
      Panel.state.info &&
      settings.get["ui.adaptElementsToSidebar"],
  );
  const isCollapsed = collapsed && Boolean(user);

  useEffect(() => {
    const handle = ({ available }: { available: number }) => {
      setAvailable(available);
    };

    network.on("pixels", handle);
    network.waitForState("pixels").then(([data]) => handle(data));

    return () => {
      network.off("pixels", handle);
    };
  }, []);

  useEffect(() => {
    setAvailablePixelsFavicon(user ? available : 0);

    return () => setAvailablePixelsFavicon(0);
  }, [available, user]);

  return (
    <>
      <motion.div
        animate={{
          x: offsetForSidebar ? sidebarWidth : 0,
          bottom: canvasMetaBottom,
        }}
        id="canvas-meta"
        className={`toolbar-box canvas-overlay-card${isCollapsed ? " canvas-meta--collapsed" : ""}`}
        transition={
          reduceMotion
            ? { duration: 0 }
            : { x: SIDEBAR_SPRING, bottom: PALETTE_LAYOUT_SPRING }
        }
      >
        {user && (
          <button
            type="button"
            className="canvas-meta-collapse"
            aria-expanded={!isCollapsed}
            aria-label={
              isCollapsed
                ? "Expand canvas information"
                : "Collapse canvas information"
            }
            title={
              isCollapsed
                ? "Expand canvas information"
                : "Collapse canvas information"
            }
            onClick={() => setCollapsed((value) => !value)}
          >
            {isCollapsed ? (
              <ChevronUp aria-hidden="true" />
            ) : (
              <ChevronDown aria-hidden="true" />
            )}
          </button>
        )}
        {canvasPosition && (
          <div
            aria-hidden={isCollapsed}
            className={`canvas-meta-row canvas-meta-collapsible-row${isCollapsed ? " is-collapsed" : ""}`}
          >
            <Crosshair className="canvas-meta-icon" aria-hidden="true" />
            <span>
              <button
                className="btn-link"
                tabIndex={isCollapsed ? -1 : undefined}
                onClick={() =>
                  Dialog.dispatch({
                    action: "OPEN_DIALOG",
                    dialog: { id: "SHARE_MODAL" },
                  })
                }
              >
                ({canvasPosition.x}, {canvasPosition.y})
              </button>
              {cursor.x !== undefined && cursor.y !== undefined && (
                <span className="canvas-meta--cursor-pos">
                  Cursor: {cursor.x}, {cursor.y}
                </span>
              )}
            </span>
          </div>
        )}
        {user && (
          <div className="canvas-meta-row">
            <Brush className="canvas-meta-icon" aria-hidden="true" />
            <span>
              Pixels: {available}/{config.canvas.pixel.maxStack}{" "}
              <PlaceCountdown available={available} />
            </span>
          </div>
        )}
        <div
          aria-hidden={isCollapsed}
          className={`canvas-meta-row canvas-meta-collapsible-row${isCollapsed ? " is-collapsed" : ""}`}
        >
          <Users className="canvas-meta-icon" aria-hidden="true" />
          <span>
            Users Online: <OnlineCount />
          </span>
        </div>
      </motion.div>
      <ShareModal />
    </>
  );
};
